import type { MoneyAmount } from 'types'
import { getMinifiedLocaleCurrencyStringFromMoneyAmount } from 'utils'
import type { Money } from 'utils/web_api.schemas'

import { calculateDiscountDisplay } from './utils'

type PriceType = Money | MoneyAmount

type OrderProductCardProps = {
  productBrand: string;
  productSlug: string;
  variantName: string;
  variantBasePrice: PriceType;
  variantDiscountedPrice: PriceType | null;
  variantThumbnailImageUrl: string;
}

// Helper to normalize price to cents for calculations
const priceToSubunits = (price: PriceType): number => {
  if ('subunits' in price) {
    return price.subunits
  }
  return parseFloat(price.value) * 100
}

// Helper to format price for display
const formatPrice = (price: PriceType): string => {
  if ('formatted_amount' in price) {
    return price.formatted_amount
  }
  return getMinifiedLocaleCurrencyStringFromMoneyAmount(price)
}

export const OrderProductCard = ({
  productBrand,
  productSlug,
  variantName,
  variantBasePrice,
  variantDiscountedPrice,
  variantThumbnailImageUrl,
}: OrderProductCardProps) => {
  const productPrice = variantDiscountedPrice ?? variantBasePrice

  return (
    <div className='p-4 rounded-lg border border-gray-200 bg-white shadow-sm'>
      <div className='flex gap-2'>
        <div className='flex-shrink-0'>
          <img
            src={variantThumbnailImageUrl}
            alt={variantName}
            className='w-20 h-20 rounded object-cover'
          />
        </div>

        <div className='flex-1 min-w-0 flex flex-col gap-1 mt-1.5'>
          <div className='flex items-baseline justify-between'>
            <p className='body-sm text-gray-500'>{productBrand}</p>
            <div className='flex items-baseline gap-2'>
              {variantDiscountedPrice !== null && (
                <span className='body-sm text-gray-500 line-through'>
                  {formatPrice(variantBasePrice)}
                </span>
              )}
              <span className='body-bold text-gray-800'>
                {formatPrice(productPrice)}
              </span>
            </div>
          </div>

          <div className='flex items-center justify-between'>
            <p className='body-bold'>{variantName}</p>
            {variantDiscountedPrice !== null && (
              <div className='inline-flex items-center gap-1 text-xs font-medium text-violet-500'>
                <span>
                  {calculateDiscountDisplay(
                    priceToSubunits(variantBasePrice),
                    priceToSubunits(variantDiscountedPrice),
                    productSlug
                  )}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
